import React, { useState, useEffect } from 'react';
import { Modal, Checkbox, Tree, Space, Button, message, Spin } from 'antd';
import { EnvironmentOutlined } from '@ant-design/icons';
import { getAvailableRegions } from '../utils/incidentExportUtils';

const GeographySelectionModal = ({ 
  visible, 
  onCancel, 
  onConfirm, 
  orgId,
  title = "Select Geographic Regions"
}) => {
  const [loading, setLoading] = useState(false);
  const [regions, setRegions] = useState({});
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [checkedKeys, setCheckedKeys] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState([]);

  // Load available regions when modal opens
  useEffect(() => {
    if (visible && orgId) {
      loadRegions();
    }
  }, [visible, orgId]);

  const loadRegions = async () => {
    try {
      setLoading(true);
      const regionsData = await getAvailableRegions(orgId);
      setRegions(regionsData);
      
      // Auto-expand all countries
      setExpandedKeys(Object.keys(regionsData));
      
    } catch (error) {
      console.error('Error loading regions:', error);
      message.error('Failed to load available regions');
    } finally {
      setLoading(false);
    }
  };

  // Convert regions data to tree format for Ant Design Tree component
  const convertToTreeData = () => {
    return Object.entries(regions).map(([country, states]) => ({
      title: (
        <Space>
          <EnvironmentOutlined />
          <strong>{country}</strong>
          <span style={{ color: '#999' }}>({states.length} states/provinces)</span>
        </Space>
      ),
      key: `country-${country}`,
      children: states.map(state => ({
        title: state,
        key: `state-${country}-${state}`,
        isLeaf: true
      }))
    }));
  };

  // Handle tree selection changes
  const handleTreeCheck = (checkedKeysValue, info) => {
    setCheckedKeys(checkedKeysValue);
    
    // Convert checked keys to selected regions format
    const selected = [];
    
    checkedKeysValue.forEach(key => {
      if (key.startsWith('country-')) {
        const country = key.replace('country-', '');
        selected.push({
          type: 'country',
          value: country,
          label: country
        });
      } else if (key.startsWith('state-')) {
        const parts = key.replace('state-', '').split('-');
        const country = parts[0];
        const state = parts.slice(1).join('-'); // Handle states with hyphens
        selected.push({
          type: 'state',
          country: country,
          value: state,
          label: `${state}, ${country}`
        });
      }
    });
    
    setSelectedRegions(selected);
  };

  // Handle "Select All" functionality
  const handleSelectAll = () => {
    const allKeys = [];
    Object.entries(regions).forEach(([country, states]) => {
      allKeys.push(`country-${country}`);
      states.forEach(state => {
        allKeys.push(`state-${country}-${state}`);
      });
    });
    
    setCheckedKeys(allKeys);
    handleTreeCheck(allKeys, {});
  };

  // Handle "Clear All" functionality
  const handleClearAll = () => {
    setCheckedKeys([]);
    setSelectedRegions([]);
  };

  // Handle confirm
  const handleConfirm = () => {
    onConfirm(selectedRegions);
  };

  const totalCountries = Object.keys(regions).length;
  const totalStates = Object.values(regions).reduce((sum, states) => sum + states.length, 0);

  return (
    <Modal
      title={
        <Space>
          <EnvironmentOutlined />
          {title}
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      onOk={handleConfirm}
      okText="Export Selected Regions"
      cancelText="Cancel"
      width={600}
      okButtonProps={{
        disabled: selectedRegions.length === 0,
        loading: loading
      }}
    >
      <div style={{ marginBottom: 16 }}>
        <p style={{ color: '#666', marginBottom: 16 }}>
          Select the geographic regions you want to include in the site status export. 
          You can select entire countries or specific states/provinces.
        </p>
        
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" />
            <p style={{ marginTop: 16 }}>Loading available regions...</p>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ color: '#666' }}>
                Available: {totalCountries} countries, {totalStates} states/provinces
              </div>
              <Space>
                <Button size="small" onClick={handleSelectAll}>
                  Select All
                </Button>
                <Button size="small" onClick={handleClearAll}>
                  Clear All
                </Button>
              </Space>
            </div>

            {Object.keys(regions).length > 0 ? (
              <div style={{ 
                maxHeight: 400, 
                overflowY: 'auto', 
                border: '1px solid #d9d9d9', 
                borderRadius: 6, 
                padding: 8 
              }}>
                <Tree
                  checkable
                  checkedKeys={checkedKeys}
                  expandedKeys={expandedKeys}
                  onCheck={handleTreeCheck}
                  onExpand={setExpandedKeys}
                  treeData={convertToTreeData()}
                  height={350}
                />
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                No regions found. Make sure your organization has sites with location data.
              </div>
            )}

            {selectedRegions.length > 0 && (
              <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6 }}>
                <strong>Selected regions ({selectedRegions.length}):</strong>
                <div style={{ marginTop: 8 }}>
                  {selectedRegions.map((region, index) => (
                    <span key={index} style={{ 
                      display: 'inline-block', 
                      margin: '2px 4px', 
                      padding: '2px 8px', 
                      backgroundColor: '#52c41a', 
                      color: 'white', 
                      borderRadius: 4,
                      fontSize: '12px'
                    }}>
                      {region.label}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default GeographySelectionModal; 